// ─────────────────────────────────────────────
//  useMyRooms — "the rooms I'm on today", shared by web and mobile.
//
//  Extracted VERBATIM from ProviderPage (S2 of the mobile redesign): the two
//  live listeners (assignments for the day + rooms) and the derivations the
//  page rendered from — role-aware my-assignments (doctors see every room they
//  supervise, CRNAs see their room(s)), each paired with its room doc (or a
//  synthetic fallback so the card still renders), the cover-a-break cards,
//  and the Phase-5.2 handoff state (awaiting / following / room chain).
//
//  The web ProviderPage and the mobile CRNA/MDA dashboards consume the SAME
//  hook, so a matching fix lands in one place. Staff pools ride along in the
//  return value so the page keeps exactly one staff listener (this hook's).
// ─────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { listenToAssignments, listenToRooms } from "../firebase/firestore";
import { myRoomAssignments } from "../domain/myRooms";
import { roomForOrRoom } from "../domain/gridTemplate";
import { resolveRoomDoctorId } from "../domain/alerts";
import { roomChainForProvider } from "../domain/conditions";
import { useAuth } from "../context/AuthContext";
import { useAlertCenter } from "../context/AlertContext";
import { useStaff } from "./useStaff";

export function useMyRooms(facilityId, dateKey) {
  const { user } = useAuth();
  const { breakRequestRoomIds } = useAlertCenter();
  const { staff, doctors, providers } = useStaff();

  // Live data for the room cards (same sources the board uses).
  const [assignments, setAssignments] = useState([]);
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    if (!facilityId) return undefined;
    return listenToAssignments(facilityId, dateKey, (snap) =>
      setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, [facilityId, dateKey]);

  useEffect(() => {
    if (!facilityId) return undefined;
    return listenToRooms(facilityId, (snap) =>
      setRooms(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, [facilityId]);

  // Role-aware: doctors get every room they supervise; CRNAs get their room(s).
  const mine = useMemo(
    () => myRoomAssignments(assignments, user, staff),
    [assignments, user, staff]
  );

  // Pair each assignment with its room doc (or a minimal synthetic room so the
  // card still renders if the room doc can't be matched by name).
  const myCards = useMemo(
    () =>
      mine.map((a) => {
        const room =
          roomForOrRoom(a.orRoom, rooms) ||
          { id: `syn-${a.id}`, name: a.orRoom || "OR", location: "other", status: "active" };
        return { key: a.id, room, assignment: a };
      }),
    [mine, rooms]
  );

  // Break requests to cover (Prompt 9): rooms with a live break request that are
  // NOT my own room. A free/unassigned provider sees these in My Rooms and can go
  // cover; the in-card "Cover break" button captures them as the cover.
  const myRoomIds = useMemo(() => new Set(myCards.map((c) => c.room.id)), [myCards]);
  const coverCards = useMemo(
    () =>
      rooms
        .filter((r) => breakRequestRoomIds.has(r.id) && !myRoomIds.has(r.id))
        .map((room) => ({
          key: `cover-${room.id}`,
          room,
          assignment: assignments.find((a) => a.orRoom === room.name) || null,
        })),
    [rooms, breakRequestRoomIds, myRoomIds, assignments]
  );

  // ── Phase 5.2: condition + handoff derivations ──
  // The real room doc I operate on (skip synthetic fallback cards).
  const myLiveCard = useMemo(
    () => myCards.find((c) => !String(c.room.id).startsWith("syn-")) || null,
    [myCards]
  );
  const myRoom = myLiveCard?.room || null;
  const myAssignment = myLiveCard?.assignment || null;
  const myDoctorId = myRoom ? resolveRoomDoctorId(myRoom, myAssignment) : null;
  // Destination room awaiting MY arrival (Mark Arrived lives here, not on the
  // dense board chip).
  const awaitingRoom = useMemo(
    () =>
      rooms.find(
        (r) => r?.followUp?.direction === "awaiting" && r.followUp.providerId === user?.uid
      ) || null,
    [rooms, user?.uid]
  );
  // My staged (not yet arrived) follow-up origin, if any.
  const followingRoom = useMemo(
    () =>
      rooms.find(
        (r) => r?.followUp?.direction === "following" && r.followUp.providerId === user?.uid
      ) || null,
    [rooms, user?.uid]
  );
  // Room chain for the shift (persisted handoffs + live hop).
  const chainSteps = useMemo(
    () => roomChainForProvider(rooms, user?.uid, dateKey),
    [rooms, user?.uid, dateKey]
  );

  return {
    myCards,
    coverCards,
    myRoom,
    myAssignment,
    myDoctorId,
    rooms,
    assignments,
    awaitingRoom,
    followingRoom,
    chainSteps,
    // Staff pools ride along so consumers don't open a second staff listener.
    staff,
    doctors,
    providers,
  };
}
